import { ALL_MARKETS } from "~/contexts/sales-data-context";
import type {
  CurrencyTotal,
  MarketMeta,
  SalesChartDatum,
  TotalSalesViewModel,
} from "./map-sales-response";

/**
 * Narrows a market-stacked view model to a single focused market: keeps the
 * per-market keys (so sparklines/tooltips still resolve) but recomputes each
 * bucket total and the panel aggregates from the focused market alone.
 */
const focusMarketModel = (
  viewModel: TotalSalesViewModel,
  marketFocus: string,
  focused: MarketMeta,
): TotalSalesViewModel => {
  const markets = [focused];
  const byMarket = viewModel.totals.byMarket.filter(
    (entry) => entry.market === marketFocus,
  );
  const revenueByCurrency: CurrencyTotal[] = byMarket
    .filter((entry) => entry.revenue != null)
    .map((entry) => ({
      currency: entry.currency,
      revenue: entry.revenue as number,
    }));
  const units = byMarket.reduce((sum, entry) => sum + entry.units, 0);

  // Recompute each bucket's combined `total` to the focused market's units so the
  // tooltip headline and any combined-display fallback reflect the selection.
  const data: SalesChartDatum[] = viewModel.data.map((datum) => {
    const value = datum[marketFocus];
    return { ...datum, total: typeof value === "number" ? value : 0 };
  });

  return {
    data,
    markets,
    formats: viewModel.formats,
    stackMode: viewModel.stackMode,
    totals: { units, revenueByCurrency, byMarket },
    peak: viewModel.peak,
  };
};

/**
 * Narrows a format-stacked (bundled) view model to a single focused market.
 * Each format segment keeps its stack but is recomputed from the focused
 * market's grain in `detail[isbn].byMarket`, so the bars, per-format tooltip
 * totals, and combined footer all reflect the one market.
 */
const focusFormatModel = (
  viewModel: TotalSalesViewModel,
  marketFocus: string,
  focused: MarketMeta,
): TotalSalesViewModel => {
  const formats = viewModel.formats ?? [];
  let unitsTotal = 0;
  let revenue = 0;
  let hasRevenue = false;

  const data: SalesChartDatum[] = viewModel.data.map((datum) => {
    const detail: SalesChartDatum["detail"] = {};
    const next: SalesChartDatum = { ...datum, detail };
    let total = 0;
    formats.forEach((format) => {
      const marketEntry = datum.detail[format.isbn]?.byMarket?.[marketFocus];
      const units = marketEntry?.units ?? 0;
      next[format.isbn] = units;
      detail[format.isbn] = {
        units,
        revenue: null,
        currency: "",
        byMarket: marketEntry ? { [marketFocus]: marketEntry } : {},
      };
      total += units;
      if (marketEntry?.revenue != null) {
        revenue += marketEntry.revenue;
        hasRevenue = true;
      }
    });
    next.total = total;
    unitsTotal += total;
    return next;
  });

  return {
    data,
    markets: [focused],
    formats,
    stackMode: "format",
    totals: {
      units: unitsTotal,
      revenueByCurrency: hasRevenue
        ? [{ currency: focused.currency, revenue }]
        : [],
      byMarket: [],
    },
    peak: viewModel.peak,
  };
};

/**
 * Narrows a Total Sales view model to a single focused market (the markets
 * toggle) for both the per-market and bundled (by-format) views. When
 * `marketFocus` is `"all"` — or the focused market is absent from the current
 * window — the view model is returned unchanged, so the caller keeps the
 * aggregated view (a defensive fallback to "all").
 *
 * Colours stay stable via the sales-info market order passed to the mapper.
 */
export const applyMarketFocus = (
  viewModel: TotalSalesViewModel,
  marketFocus: string,
): TotalSalesViewModel => {
  if (marketFocus === ALL_MARKETS) {
    return viewModel;
  }

  const focused = viewModel.markets.find((meta) => meta.market === marketFocus);
  if (!focused) {
    return viewModel;
  }

  return viewModel.stackMode === "format"
    ? focusFormatModel(viewModel, marketFocus, focused)
    : focusMarketModel(viewModel, marketFocus, focused);
};
